import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Card, CardBody, Field, TextInput } from "@odingard/design-system";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";

type Interval = "month" | "year";

export function StartTrial() {
  const { register, refresh, setActiveOrg, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sku = params.get("sku");
  const interval: Interval = params.get("interval") === "year" ? "year" : "month";
  const isBuy = Boolean(sku);
  // Which app the visitor came in for (`?app=app-policy`), set by the corporate
  // site's product-page CTAs. Resolved against the catalog so the offer names
  // the app the visitor clicked rather than a hardcoded one.
  const appSlug = params.get("app");

  const [step, setStep] = useState<"account" | "org">("account");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [orgName, setOrgName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [verifyLink, setVerifyLink] = useState<string | null>(null);
  const [verifySent, setVerifySent] = useState(false);
  const [trialApp, setTrialApp] = useState<{ slug: string; name: string; days: number } | null>(null);

  // A user who already has a session (e.g. they created their account but never
  // finished org setup) should resume at the organization step, not be asked to
  // register again.
  useEffect(() => {
    if (user) setStep("org");
  }, [user]);

  // An app that is unknown or not self-service trialable leaves `trialApp` null,
  // which falls back to the generic offer — the page never names an app it
  // cannot actually provision.
  useEffect(() => {
    if (!appSlug || isBuy) return;
    let cancelled = false;
    api
      .catalogApplication(appSlug)
      .then((app) => {
        if (!cancelled && app.trial) setTrialApp({ slug: app.slug, name: app.name, days: app.trial.days });
      })
      .catch(() => {
        /* Unknown app: keep the generic offer. */
      });
    return () => {
      cancelled = true;
    };
  }, [appSlug, isBuy]);

  async function createAccount(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const v = await register(email, password, displayName);
      setVerifySent(v.verificationSent);
      setVerifyLink(v.verificationLink ?? null);
      setStep("org");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create your account.");
    } finally {
      setBusy(false);
    }
  }

  async function createOrg(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (isBuy && sku) {
        // Purchase intent: create the org, then go straight to Stripe-hosted checkout.
        const org = await api.createOrganization(orgName);
        setActiveOrg(org.id);
        const { url } = await api.createStripeCheckout(org.id, { sku, interval });
        window.location.assign(url);
        return;
      }
      // Trial intent: create org + provision the trial atomically.
      const org = await api.createOrganization(orgName, {
        withTrial: true,
        ...(trialApp ? { app: trialApp.slug } : {}),
      });
      setActiveOrg(org.id);
      await refresh();
      navigate("/studio");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not finish setting up your organization.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <Link to="/" className="brand" style={{ justifyContent: "center", marginBottom: "var(--ods-space-5)" }}>
          <span className="brand__mark">O</span> Odingard Studio
        </Link>
        <Card>
          <CardBody>
            <h1 style={{ fontSize: "var(--ods-text-xl)", marginTop: 0 }}>
              {step === "account"
                ? isBuy
                  ? "Create your account"
                  : trialApp
                    ? `Start your ${trialApp.name} trial`
                    : "Start your free trial"
                : "Name your organization"}
            </h1>
            <p style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)", marginTop: 0 }}>
              Step {step === "account" ? "1" : "2"} of 2
              {isBuy
                ? " · you'll complete payment on Stripe next"
                : ` · ${trialApp ? `${trialApp.days}-day ${trialApp.name}` : "14-day Contract AI"} trial, no card required`}
            </p>
            {error && (
              <div className="banner banner--warning" role="alert">
                {error}
              </div>
            )}
            {step === "org" && (verifySent || verifyLink) && (
              <div className="banner banner--info" role="status">
                {verifySent ? (
                  <>We sent a verification link to <strong>{email}</strong>. You can verify anytime &mdash; it won&apos;t block setup.</>
                ) : (
                  <>
                    Confirm your email to secure your account:{" "}
                    <a href={verifyLink ?? "#"}>verify now</a>.
                  </>
                )}
              </div>
            )}
            {step === "account" ? (
              <form onSubmit={createAccount}>
                <Field label="Full name">
                  {(id) => <TextInput id={id} value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />}
                </Field>
                <Field label="Work email">
                  {(id) => <TextInput id={id} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />}
                </Field>
                <Field label="Password" help="At least 8 characters.">
                  {(id) => (
                    <TextInput
                      id={id}
                      type="password"
                      autoComplete="new-password"
                      minLength={8}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  )}
                </Field>
                <Button type="submit" block disabled={busy}>
                  {busy ? "Creating…" : "Create account"}
                </Button>
              </form>
            ) : (
              <form onSubmit={createOrg}>
                <Field label="Organization name">
                  {(id) => <TextInput id={id} value={orgName} onChange={(e) => setOrgName(e.target.value)} required />}
                </Field>
                <Button type="submit" block disabled={busy}>
                  {busy ? (isBuy ? "Redirecting to checkout…" : "Setting up…") : isBuy ? "Continue to payment" : "Start trial & enter Studio"}
                </Button>
              </form>
            )}
          </CardBody>
        </Card>
        <p style={{ textAlign: "center", fontSize: "var(--ods-text-sm)", color: "var(--ods-color-muted)", marginTop: "var(--ods-space-4)" }}>
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
